import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, CATEGORY_META, getToken, getWsBase, formatApiError, timeAgo } from "../lib/api";
import { useAuth } from "../lib/auth";
import { MapPin, Clock, ArrowLeft, Send, CheckCircle2, Star, MessageSquare, Flag, Trash2, AlertTriangle } from "lucide-react";

export default function TaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // chat
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const wsRef = useRef(null);
  const chatEndRef = useRef(null);

  // complete dialog
  const [showComplete, setShowComplete] = useState(false);
  const [rating, setRating] = useState(5);
  const [review, setReview] = useState("");

  // cancel dialog
  const [showCancel, setShowCancel] = useState(false);
  const [cancelResult, setCancelResult] = useState(null);

  // report dialog
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const [reportDone, setReportDone] = useState(false);
  const [reportErr, setReportErr] = useState(null);

  const loadTask = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tasks/${id}`);
      setTask(data.task);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Task not found");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadTask(); }, [loadTask]);

  // Load messages if I'm part of the task
  const partOfTask = task && user && (task.poster_id === user.id || task.claimer_id === user.id);

  useEffect(() => {
    if (!partOfTask) return;
    let active = true;
    api.get(`/tasks/${id}/messages`).then(({ data }) => {
      if (active) setMessages(data.messages || []);
    }).catch(() => {});
    return () => { active = false; };
  }, [id, partOfTask]);

  // WebSocket connection
  useEffect(() => {
    if (!partOfTask) return;
    const token = getToken();
    if (!token) return;
    const wsUrl = `${getWsBase()}/ws/chat/${id}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === "message" && data.message) {
          setMessages((prev) => {
            if (prev.some((m) => m.id === data.message.id)) return prev;
            return [...prev, data.message];
          });
        }
      } catch (e) {}
    };
    ws.onerror = () => {};
    return () => {
      try { ws.close(); } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, partOfTask]);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const claim = async () => {
    setActionLoading(true);
    try {
      const { data } = await api.post(`/tasks/${id}/claim`);
      setTask(data.task);
      await refresh();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const sendMessage = (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || !wsRef.current || wsRef.current.readyState !== 1) {
      // fallback http
      if (t) {
        api.post(`/tasks/${id}/messages`, { text: t }).then(({ data }) => {
          setMessages((prev) => prev.some((m) => m.id === data.message.id) ? prev : [...prev, data.message]);
        }).catch(() => {});
        setText("");
      }
      return;
    }
    wsRef.current.send(JSON.stringify({ text: t }));
    setText("");
  };

  const completeTask = async () => {
    setActionLoading(true);
    try {
      const { data } = await api.post(`/tasks/${id}/complete`, { rating, review });
      setTask(data.task);
      await refresh();
      setShowComplete(false);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const cancelTask = async () => {
    setActionLoading(true);
    setErr(null);
    try {
      const { data } = await api.delete(`/tasks/${id}`);
      setCancelResult(data);
      // Brief celebration / refund confirmation, then bounce
      setTimeout(() => navigate("/my-tasks"), 1800);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      setShowCancel(false);
    } finally {
      setActionLoading(false);
    }
  };

  const submitReport = async () => {
    if (!reportReason) { setReportErr("Please pick a reason."); return; }
    setActionLoading(true);
    setReportErr(null);
    try {
      await api.post(`/tasks/${id}/report`, { reason: reportReason, details: reportDetails });
      setReportDone(true);
      setTimeout(() => { setShowReport(false); setReportDone(false); setReportReason(""); setReportDetails(""); }, 2000);
    } catch (e) {
      setReportErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <div className="text-center py-20 text-[var(--cs-ink-2)] font-bold uppercase tracking-widest text-sm">Loading…</div>;
  if (err && !task) return (
    <div className="max-w-xl mx-auto brut-card p-6 text-center">
      <Flag className="mx-auto text-red-600" size={28} />
      <div className="font-display text-2xl font-extrabold mt-2">{err}</div>
      <Link to="/dashboard" className="brut-btn mt-5 inline-flex" data-testid="back-to-dashboard">
        <ArrowLeft size={16} /> Back to browse
      </Link>
    </div>
  );
  if (!task) return null;

  const meta = CATEGORY_META[task.category] || { label: task.category, className: "" };
  const isPoster = user?.id === task.poster_id;
  const isClaimer = user?.id === task.claimer_id;
  const isAdmin = !!user?.is_admin;
  const canClaim = task.status === "open" && !isPoster;
  const canComplete = task.status === "claimed" && (isPoster || isClaimer);
  const canCancel = (isPoster || isAdmin) && (task.status === "open" || task.status === "pending_payment");

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="task-detail-page">
      {/* Left: details */}
      <div className="lg:col-span-3 space-y-5">
        <button onClick={() => navigate(-1)} data-testid="task-back-btn" className="brut-btn brut-btn-ghost">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="brut-card p-6 sm:p-8">
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <span className={`brut-chip ${meta.className}`}>{meta.label}</span>
            <StatusBadge status={task.status} />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95]">
            {task.title}
          </h1>
          <div className="mt-5 flex items-end gap-3">
            <div className="font-display tabular text-6xl sm:text-7xl font-black leading-none">
              <span className="text-[var(--cs-money-2)]">$</span>{Math.round(task.price)}
            </div>
            <div className="mb-1 text-sm text-[var(--cs-ink-2)]">
              <div>Fee ${task.fee.toFixed(2)}</div>
              <div className="font-bold text-[var(--cs-ink)]">Payout ${task.payout.toFixed(2)}</div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <Detail icon={MapPin} label="Location" value={`${task.location_text} · ${task.distance_miles}mi`} />
            <Detail icon={Clock} label="Time" value={task.time_window} />
            <Detail icon={MessageSquare} label="Posted by" value={task.poster_name} />
          </div>

          <div className="mt-6 pt-5 border-t-2" style={{ borderColor: "var(--cs-ink)" }}>
            <div className="text-xs uppercase tracking-widest font-bold text-[var(--cs-ink-2)]">Details</div>
            <p className="mt-2 text-[var(--cs-ink)] leading-relaxed whitespace-pre-line">{task.description}</p>
          </div>

          {err && (
            <div className="mt-4 brut-card p-3 bg-red-50 text-red-700 text-sm font-semibold" style={{ boxShadow: "3px 3px 0 0 #b91c1c", borderColor: "#b91c1c" }}>
              {err}
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            {canClaim && (
              <button
                onClick={claim}
                disabled={actionLoading}
                className="brut-btn brut-btn-money text-base px-6 py-4"
                data-testid="claim-task-button"
              >
                {actionLoading ? "Claiming…" : "Claim this task"}
              </button>
            )}
            {canComplete && (
              <button
                onClick={() => setShowComplete(true)}
                className="brut-btn brut-btn-flame text-base px-6 py-4"
                data-testid="complete-task-button"
              >
                <CheckCircle2 size={18} /> Mark complete
              </button>
            )}
            {task.status === "completed" && (
              <div className="brut-chip bg-[var(--cs-money)]">
                <CheckCircle2 size={14} /> Completed {timeAgo(task.completed_at)}
              </div>
            )}
            {isPoster && task.status === "open" && (
              <Link to="/my-tasks" className="brut-btn" data-testid="manage-task">Back to my tasks</Link>
            )}
            {canCancel && (
              <button
                onClick={() => setShowCancel(true)}
                className="brut-btn"
                style={{ background: "#fee2e2", borderColor: "#b91c1c", color: "#b91c1c", boxShadow: "3px 3px 0 0 #b91c1c" }}
                data-testid="cancel-task-button"
              >
                <Trash2 size={16} /> Cancel task{isAdmin && !isPoster ? " (admin)" : ""}
              </button>
            )}
            {user && !isPoster && (
              <button
                onClick={() => setShowReport(true)}
                className="brut-btn brut-btn-ghost text-sm"
                data-testid="report-task-button"
              >
                <Flag size={14} /> Report
              </button>
            )}
          </div>

          {task.status === "completed" && (
            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              {task.tasker_rating && (
                <RatingBlock who={`${task.claimer_name} (tasker)`} rating={task.tasker_rating} review={task.tasker_review} />
              )}
              {task.poster_rating && (
                <RatingBlock who={`${task.poster_name} (poster)`} rating={task.poster_rating} review={task.poster_review} />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right: chat */}
      <div className="lg:col-span-2">
        <div className="brut-card overflow-hidden flex flex-col h-[560px] sticky top-20">
          <div className="px-5 py-4 border-b-2 flex items-center justify-between" style={{ borderColor: "var(--cs-ink)" }}>
            <div>
              <div className="text-xs uppercase tracking-widest font-bold text-[var(--cs-ink-2)]">Coordinate</div>
              <div className="font-display text-xl font-extrabold">
                {partOfTask ? (isPoster ? task.claimer_name || "Waiting for claimer" : task.poster_name) : "Locked"}
              </div>
            </div>
            <span className="brut-chip bg-[var(--cs-yellow)]">Live</span>
          </div>

          {!partOfTask ? (
            <div className="flex-1 flex items-center justify-center text-center px-6">
              <div>
                <MessageSquare size={28} className="mx-auto text-[var(--cs-ink-2)]" />
                <div className="mt-2 font-bold">Claim the task to chat.</div>
                <div className="text-sm text-[var(--cs-ink-2)]">Realtime messages once you're matched.</div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-[var(--cs-bg-2)]" data-testid="chat-messages">
                {messages.length === 0 && (
                  <div className="text-center text-[var(--cs-ink-2)] text-sm mt-8">Say hi 👋</div>
                )}
                {messages.map((m) => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div
                        className="max-w-[80%] px-3 py-2 rounded-xl border-2 text-sm"
                        style={{
                          borderColor: "var(--cs-ink)",
                          background: mine ? "var(--cs-money)" : "#fff",
                          boxShadow: "2px 2px 0 0 var(--cs-ink)",
                        }}
                      >
                        <div className="font-bold text-[10px] uppercase tracking-widest opacity-70">
                          {mine ? "You" : m.sender_name} · {timeAgo(m.created_at)}
                        </div>
                        <div className="mt-0.5 whitespace-pre-wrap break-words">{m.text}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={chatEndRef} />
              </div>
              <form onSubmit={sendMessage} className="p-3 border-t-2 flex gap-2" style={{ borderColor: "var(--cs-ink)" }}>
                <input
                  data-testid="chat-input"
                  className="brut-input flex-1 py-2.5"
                  placeholder="Type a message…"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                />
                <button type="submit" data-testid="chat-send-btn" className="brut-btn brut-btn-ink !px-3">
                  <Send size={18} />
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      {/* Cancel confirmation dialog */}
      {showCancel && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-5" data-testid="cancel-dialog">
          <div className="brut-card p-6 max-w-md w-full bg-white pop-in">
            {!cancelResult ? (
              <>
                <div className="flex items-start gap-3">
                  <div className="brut-card p-2 bg-red-50" style={{ borderColor: "#b91c1c", boxShadow: "3px 3px 0 0 #b91c1c" }}>
                    <AlertTriangle className="text-red-600" size={22} />
                  </div>
                  <div>
                    <h3 className="font-display text-2xl font-black">Cancel this task?</h3>
                    <p className="text-[var(--cs-ink-2)] text-sm mt-1">
                      The task will be removed from the feed.
                      {task.status !== "pending_payment" && " If you've already paid, you'll be auto-refunded to your card (5–10 business days)."}
                    </p>
                  </div>
                </div>
                {err && (
                  <div className="mt-4 brut-card p-3 bg-red-50 text-red-700 text-sm font-semibold" style={{ boxShadow: "3px 3px 0 0 #b91c1c", borderColor: "#b91c1c" }}>
                    {err}
                  </div>
                )}
                <div className="mt-5 flex justify-end gap-2">
                  <button onClick={() => setShowCancel(false)} className="brut-btn" data-testid="cancel-cancel">
                    Keep task
                  </button>
                  <button
                    onClick={cancelTask}
                    disabled={actionLoading}
                    className="brut-btn"
                    style={{ background: "#b91c1c", color: "#fff", borderColor: "#7f1d1d", boxShadow: "3px 3px 0 0 #7f1d1d" }}
                    data-testid="confirm-cancel"
                  >
                    {actionLoading ? "Cancelling…" : "Cancel & refund"}
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center">
                <CheckCircle2 className="mx-auto text-[var(--cs-money-2)]" size={42} />
                <h3 className="font-display text-2xl font-black mt-3">Task cancelled.</h3>
                {cancelResult.refund?.refunded ? (
                  <p className="text-[var(--cs-ink-2)] text-sm mt-2">
                    ${Number(cancelResult.refund.amount).toFixed(2)} refunded to your card.
                  </p>
                ) : (
                  <p className="text-[var(--cs-ink-2)] text-sm mt-2">No charge to refund.</p>
                )}
                <p className="text-xs text-[var(--cs-ink-2)] mt-3">Taking you back to your tasks…</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Report dialog */}
      {showReport && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-5" data-testid="report-dialog">
          <div className="brut-card p-6 max-w-md w-full bg-white pop-in">
            {!reportDone ? (
              <>
                <div className="flex items-start gap-3">
                  <div className="brut-card p-2 bg-red-50" style={{ borderColor: "#b91c1c", boxShadow: "3px 3px 0 0 #b91c1c" }}>
                    <Flag className="text-red-600" size={22} />
                  </div>
                  <div>
                    <h3 className="font-display text-2xl font-black">Report this task</h3>
                    <p className="text-[var(--cs-ink-2)] text-sm mt-1">Our team reviews every report. Thanks for keeping CashStreak safe.</p>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  {["Scam or fraud", "Inappropriate content", "Spam or misleading", "Harassment or abusive behavior", "Dangerous or illegal task", "Other"].map((r) => (
                    <button
                      type="button"
                      key={r}
                      onClick={() => setReportReason(r)}
                      data-testid={`report-reason-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className={`brut-btn w-full justify-start text-sm !py-2.5 ${reportReason === r ? "brut-btn-ink" : ""}`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
                <textarea
                  data-testid="report-details-input"
                  className="brut-input mt-3 min-h-[70px]"
                  placeholder="Anything else we should know? (optional)"
                  maxLength={500}
                  value={reportDetails}
                  onChange={(e) => setReportDetails(e.target.value)}
                />
                {reportErr && (
                  <div className="mt-3 brut-card p-3 bg-red-50 text-red-700 text-sm font-semibold" style={{ boxShadow: "3px 3px 0 0 #b91c1c", borderColor: "#b91c1c" }}>
                    {reportErr}
                  </div>
                )}
                <div className="mt-5 flex justify-end gap-2">
                  <button onClick={() => { setShowReport(false); setReportErr(null); }} className="brut-btn" data-testid="report-cancel">
                    Cancel
                  </button>
                  <button
                    onClick={submitReport}
                    disabled={actionLoading}
                    className="brut-btn"
                    style={{ background: "#b91c1c", color: "#fff", borderColor: "#7f1d1d", boxShadow: "3px 3px 0 0 #7f1d1d" }}
                    data-testid="report-submit"
                  >
                    {actionLoading ? "Sending…" : "Submit report"}
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center" data-testid="report-success">
                <CheckCircle2 className="mx-auto text-[var(--cs-money-2)]" size={42} />
                <h3 className="font-display text-2xl font-black mt-3">Report received.</h3>
                <p className="text-[var(--cs-ink-2)] text-sm mt-2">Our team will review it shortly. Thank you.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Complete dialog */}
      {showComplete && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-5" data-testid="complete-dialog">
          <div className="brut-card p-6 max-w-md w-full bg-white pop-in">
            <h3 className="font-display text-2xl font-black">Rate {isPoster ? task.claimer_name : task.poster_name}</h3>
            <p className="text-[var(--cs-ink-2)] text-sm mt-1">
              {isPoster ? "How was their work?" : "How was the poster?"}
            </p>
            <div className="mt-5 flex items-center justify-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  type="button"
                  key={n}
                  onClick={() => setRating(n)}
                  data-testid={`star-${n}`}
                  className="p-1"
                >
                  <Star size={36} fill={n <= rating ? "#FFB703" : "transparent"} className={n <= rating ? "text-[var(--cs-flame-2)]" : "text-[var(--cs-ink-2)]"} strokeWidth={2.5} />
                </button>
              ))}
            </div>
            <textarea
              data-testid="review-input"
              className="brut-input mt-4 min-h-[80px]"
              placeholder="Leave a quick note (optional)"
              maxLength={400}
              value={review}
              onChange={(e) => setReview(e.target.value)}
            />
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShowComplete(false)} className="brut-btn" data-testid="cancel-complete">
                Cancel
              </button>
              <button onClick={completeTask} disabled={actionLoading} className="brut-btn brut-btn-money" data-testid="confirm-complete">
                {actionLoading ? "Saving…" : "Submit & complete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ icon: Icon, label, value }) {
  return (
    <div className="brut-card p-3">
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--cs-ink-2)] flex items-center gap-1">
        <Icon size={12} /> {label}
      </div>
      <div className="font-bold mt-1">{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    open: { label: "Open", bg: "var(--cs-money)" },
    pending_payment: { label: "Awaiting payment", bg: "#94A3B8", fg: "#fff" },
    claimed: { label: "Claimed", bg: "var(--cs-flame-2)" },
    completed: { label: "Completed", bg: "var(--cs-ink)", fg: "#fff" },
    cancelled: { label: "Cancelled", bg: "#94A3B8" },
  };
  const m = map[status] || map.open;
  return (
    <span className="brut-chip" style={{ background: m.bg, color: m.fg || "var(--cs-ink)" }}>
      {m.label}
    </span>
  );
}

function RatingBlock({ who, rating, review }) {
  return (
    <div className="brut-card p-4">
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--cs-ink-2)]">{who}</div>
      <div className="mt-1 flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star key={n} size={16} fill={n <= rating ? "#FFB703" : "transparent"} className={n <= rating ? "text-[var(--cs-flame-2)]" : "text-[var(--cs-ink-2)]"} strokeWidth={2.5} />
        ))}
        <span className="ml-1 font-bold tabular">{rating}/5</span>
      </div>
      {review && <p className="mt-2 text-sm text-[var(--cs-ink-2)]">"{review}"</p>}
    </div>
  );
}
